import {
  ProgramSubscriptionBody,
  SubscriptionDTO,
  SubscriptionByUserProps,
  SubscriptionLineItemsByUserResponse,
  SubscriptionLineItemsProps,
  SubscriptionLineItemsResponse,
  UserBillingSubscriptions,
  Request,
  CancellationReason,
  BaseEnumerableResponse,
  BootcampSubscriptionReassignRequestDTO,
  SubscriptionReassignDTO,
} from "@campgladiator/cg-common.types.types";
import { fetchRequest } from "@campgladiator/cg-common.api.fetch-request";
import { getApiGatewayUrl } from "@campgladiator/cg-common.config.config";
import { generateQueryString } from "@campgladiator/cg-common.utility.objects";

export type UpgradeSubscriptionArgs = {
  couponCode: string;
  productId: string;
  productPrice: string;
  programId?: string;
  programScheduleId?: string;
  servicingTrainer?: string;
  subscriptionId: string;
  recommit?: boolean;
  token?: string;
};

export type UpdateSubscriptionServiceTrainerArgs = {
  stripeSubscriptionId: string;
  subscriptionId: string;
  trainerId: string;
};

export type FetchSubscriptionsByArgs = {
  startDateTime?: string;
  endDateTime?: string;
  productId?: string;
  salesRep?: string;
  servicingTrainer?: string;
  status?: string;
  programId?: string;
  programScheduleId?: string;
  includeCanceled?: boolean;
  page?: number;
  size?: number;
};

export type SubscriptionByTrainerAdvancedSearchProps = {
  coupon?: string[];
  endDateTime?: string;
  includeCanceled?: boolean;
  inContract?: boolean;
  isAnniversaryCouponApplied?: boolean;
  isArchived?: boolean;
  orderBy?: string;
  page: number;
  productId?: SubscriptionDTO["productId"];
  productSubType?: SubscriptionDTO["productSubType"];
  productType?: SubscriptionDTO["productType"];
  programId?: string;
  programScheduleId?: string;
  salesRep?: string;
  servicingTrainer?: string;
  size: number;
  sort?: string;
  startDateTime?: string;
  status?: SubscriptionDTO["status"][];
  trainerId?: string;
  trialPeriodDaysEqualsTo?: number;
  trialPeriodDaysGreaterThan?: number;
  upgradedFrom?: boolean;
  userIds?: string[];
};

const unknownErrorMessage = "An unknown error occurred";

export const fetchSubscriptions = async (
  args: ProgramSubscriptionBody
): Promise<SubscriptionDTO> => {
  const endpoint = "/billing/v1/subscriptions";
  const method = "POST";
  const body = JSON.stringify(args);

  const request: Request = {
    requestConfig: {
      method,
      url: { baseUrl: getApiGatewayUrl(), endpoint },
    },
    body,
  };

  try {
    const response = await fetchRequest({ request });
    if (!response.ok) throw new Error(await response.json());
    return (await response.json()) as SubscriptionDTO;
  } catch (error) {
    let errorMessage = error instanceof Error ? error.message : error;
    errorMessage = errorMessage || "An unknown error occurred";

    throw new Error(errorMessage as string);
  }
};

export const fetchSubscriptionReassigns = async (
  page: number,
  size: number,
  token: string = ""
) => {
  const endpoint = `/billing/v1/subscription-reassigns?page=${page}&size=${size}`;
  const method = "GET";

  try {
    const request: Request = {
      requestConfig: {
        method,
        authToken: token,
        url: { baseUrl: getApiGatewayUrl(), endpoint },
      },
    };
    const response = await fetchRequest({ request });
    const result = await response.json();
    return response.ok ? result : Promise.reject(new Error(result.error));
  } catch (error) {
    let errorMessage = error instanceof Error ? error.message : error;
    errorMessage = errorMessage || unknownErrorMessage;

    throw new Error(errorMessage as string);
  }
};

export const createSubscriptionReassignForBootcamp = async (
  args: BootcampSubscriptionReassignRequestDTO,
  token: string
): Promise<SubscriptionReassignDTO> => {
  const endpoint = "/billing/v1/subscription-reassigns/bootcamp";
  const method = "POST";
  const body = JSON.stringify(args);

  const request: Request = {
    requestConfig: {
      method,
      authToken: token,
      url: { baseUrl: getApiGatewayUrl(), endpoint },
    },
    body,
  };

  try {
    const response = await fetchRequest({ request });
    if (!response.ok) throw new Error(await response.json());
    return (await response.json()) as SubscriptionReassignDTO;
  } catch (error) {
    let errorMessage = error instanceof Error ? error.message : error;
    errorMessage = errorMessage || "An unknown error occurred";

    throw new Error(errorMessage as string);
  }
};

export const fetchSubscriptionsByParameters = async (
  args: FetchSubscriptionsByArgs
): Promise<BaseEnumerableResponse<SubscriptionDTO>> => {
  const endpoint = "/billing/v1/subscriptions";
  const method = "GET";
  const params = args ? `?${generateQueryString(args)}` : undefined;

  const request: Request = {
    requestConfig: {
      method,
      url: { baseUrl: getApiGatewayUrl(), endpoint, params },
    },
  };
  try {
    const response = await fetchRequest({ request });
    if (!response.ok) throw new Error(await response.json());
    return (await response.json()) as BaseEnumerableResponse<SubscriptionDTO>;
  } catch (error) {
    let errorMessage = error instanceof Error ? error.message : error;
    errorMessage = errorMessage || "An unknown error occurred";

    throw new Error(errorMessage as string);
  }
};

export const fetchSubscriptionsByUser = async ({
  userId,
  size = 1000,
  page = 0,
}: SubscriptionByUserProps): Promise<SubscriptionLineItemsByUserResponse> => {
  const endpoint = `/billing/v1/users/${userId}/subscriptions?page=${page}&size=${size}`;
  const method = "GET";

  try {
    const request: Request = {
      requestConfig: {
        method,
        url: { baseUrl: getApiGatewayUrl(), endpoint },
      },
    };

    const response = await fetchRequest({ request });
    const result = await response.json();

    return response.ok ? result : Promise.reject(new Error(result.error));
  } catch (error) {
    let errorMessage = error instanceof Error ? error.message : error;
    errorMessage = errorMessage || unknownErrorMessage;

    throw new Error(errorMessage as string);
  }
};

export const fetchSubscriptionsByUserId = async (
  userId: string
): Promise<UserBillingSubscriptions[]> => {
  const endpoint = `/billing/v1/users/${userId}/subscriptions/summaries`;
  const method = "GET";

  try {
    const request: Request = {
      requestConfig: {
        method,
        url: { baseUrl: getApiGatewayUrl(), endpoint },
      },
    };
    const response = await fetchRequest({ request });
    const result = await response.json();
    return response.ok ? result : Promise.reject(new Error(result.error));
  } catch (error) {
    let errorMessage = error instanceof Error ? error.message : error;
    errorMessage = errorMessage || unknownErrorMessage;

    throw new Error(errorMessage as string);
  }
};

export const fetchSubscriptionLineItems = async ({
  subscriptionId,
  size = 1000,
  page = 0,
}: SubscriptionLineItemsProps): Promise<SubscriptionLineItemsResponse> => {
  const endpoint = `/billing/v1/subscriptions/${subscriptionId}/line-items?page=${page}&size=${size}`;
  const method = "GET";

  try {
    const request: Request = {
      requestConfig: {
        method,
        url: { baseUrl: getApiGatewayUrl(), endpoint },
      },
    };
    const response = await fetchRequest({ request });
    const result = await response.json();

    return response.ok ? result : Promise.reject(new Error(result.error));
  } catch (error) {
    let errorMessage = error instanceof Error ? error.message : error;
    errorMessage = errorMessage || unknownErrorMessage;

    throw new Error(errorMessage as string);
  }
};

export const cancelSubscription = async (
  subscriptionId: string,
  cancelAt: string,
  reason?: CancellationReason,
  token?: string
): Promise<boolean> => {
  const endpoint = `/billing/v1/subscriptions/${subscriptionId}/cancel?cancelAt=${cancelAt}&reason=${
    reason ?? ""
  }`;
  const method = "PUT";

  try {
    const request: Request = {
      requestConfig: {
        method,
        url: { baseUrl: getApiGatewayUrl(), endpoint },
        authToken: token,
      },
    };
    const response = await fetchRequest({ request });
    if (!response.ok) throw new Error(await response.json());
    return true;
  } catch (error) {
    let errorMessage = error instanceof Error ? error.message : error;
    errorMessage = errorMessage || "An unknown error occurred";

    throw new Error(errorMessage as string);
  }
};

export const upgradeSubscription = async ({
  subscriptionId,
  productId,
  programId,
  programScheduleId,
  productPrice,
  servicingTrainer,
  couponCode,
  recommit,
  token,
}: UpgradeSubscriptionArgs): Promise<SubscriptionDTO> => {
  const endpoint = `/billing/v1/subscriptions/${subscriptionId}/upgrade`;
  const method = "PUT";
  const body = JSON.stringify({
    productId,
    productPrice,
    servicingTrainer,
    programId,
    programScheduleId,
    couponCode,
    recommit,
  });
  try {
    const request: Request = {
      requestConfig: {
        method,
        url: { baseUrl: getApiGatewayUrl(), endpoint },
        authToken: token,
      },
      body,
    };
    const response = await fetchRequest({ request });

    if (!response.ok) throw new Error(await response.json());
    const result = await response.json();
    return result;
  } catch (error) {
    let errorMessage = error instanceof Error ? error.message : error;
    errorMessage = errorMessage || "An unknown error occurred";

    throw new Error(errorMessage as string);
  }
};

export const updateServiceTrainer = async ({
  stripeSubscriptionId,
  subscriptionId,
  trainerId,
}: UpdateSubscriptionServiceTrainerArgs): Promise<boolean> => {
  const endpoint = `/billing/v1/subscriptions/${subscriptionId}`;
  const method = "PUT";
  const body = JSON.stringify({
    servicingTrainer: trainerId,
    stripeSubscriptionId,
  });
  try {
    const request: Request = {
      requestConfig: {
        method,
        url: { baseUrl: getApiGatewayUrl(), endpoint },
      },
      body,
    };
    const response = await fetchRequest({ request });

    if (!response.ok) throw new Error(await response.json());
    return true;
  } catch (error) {
    let errorMessage = error instanceof Error ? error.message : error;
    errorMessage = errorMessage || "An unknown error occurred";

    throw new Error(errorMessage as string);
  }
};

export const fetchSubscriptionsByTrainerAdvancedSearch = async (
  args: SubscriptionByTrainerAdvancedSearchProps
) => {
  const request: Request = {
    requestConfig: {
      method: "POST",
      url: {
        baseUrl: getApiGatewayUrl(),
        endpoint: "/billing/v1/subscriptions/search",
      },
    },
    body: JSON.stringify(args),
  };

  const response = await fetchRequest({ request });
  const result = await response.json();

  return response.ok
    ? (result as BaseEnumerableResponse<SubscriptionDTO>)
    : Promise.reject(result.message || result.error || "unknown error");
};
